import { useRef } from 'react';
import useFetch from 'use-http';
import { useSelector } from 'react-redux';
import getLLEsubdomain from '../helpers/getLLEsubdomain';

const PAYMENT_ROUTE = /\/api\/v1\/payment\/(?:resolve|reject)(?:[/?]|$)/;

export default function useShopifyApi() {
  const store = useSelector(state => ({
    shopify: state.technical?.shopify,
    checkoutLookupData: state.personal?.checkoutLookupData,
    instoreVccFlow: state.technical?.instoreVccFlow,
  }));
  // Shopify gets the retailer token from its checkout lookup; in-store VCC has none, so it uses the
  // retailer public token from /api/v3/instore/location/.
  const retailerToken = store.instoreVccFlow?.isInstoreVccFlow
    ? store.instoreVccFlow.publicToken
    : store.checkoutLookupData?.retailer_token;
  // In-store, every call takes the location JWT instead of the public token, except the payment
  // resolve/reject endpoints, which are unchanged. A ref, so the interceptor below always reads the latest value.
  const instoreJwtRef = useRef(null);
  instoreJwtRef.current = store.instoreVccFlow?.isInstoreVccFlow ? store.instoreVccFlow.token : null;

  const { post, loading, error, get, response, patch } = useFetch('https://', {
    cachePolicy: 'no-cache',
    headers: {
      Authorization: `Bearer ${retailerToken}`,
    },
    interceptors: {
      // use-http passes only the host as `url`; the endpoint we pass to post() arrives as `route`.
      request: async ({ options, route }) => {
        if (instoreJwtRef.current && !PAYMENT_ROUTE.test(String(route))) {
          options.headers = { ...options.headers, Authorization: `Bearer ${instoreJwtRef.current}` };
        }
        return options;
      },
    },
  });
  const host = window.location?.host;
  const isLLE = host?.includes('sandbox') || host?.includes('qa02') || host?.includes('preprod');

  const applicationRequest = async payload => {
    return post(`${getLLEsubdomain('dot')}katapult.com/api/v3/application/`, payload);
  };

  const preapproveRequest = async newUserAppUid => {
    return get(`${getLLEsubdomain('dot')}katapult.com/api/v3/application/${newUserAppUid}/preapprove/`);
  };

  const preapproveTmRequest = async (uid, tmSessionId) => {
    return get(
      `${getLLEsubdomain('dot')}katapult.com/api/v3/application/${uid}/preapprove/?tmx_session_id=${tmSessionId}`,
    );
  };

  const initializeRequest = async payload => {
    return post(`${getLLEsubdomain('dot')}katapult.com/api/v3/application/initialize/`, payload);
  };

  const priceDisclosureRequest = async checkoutUid => {
    return get(`${getLLEsubdomain('dot')}katapult.com/api/v3/application/${checkoutUid}/contract/?type=pricing`);
  };

  const leaseAgreementRequest = async checkoutUid => {
    return get(`${getLLEsubdomain('dot')}katapult.com/api/v3/application/${checkoutUid}/contract/`);
  };

  const resolveRequest = async payload => {
    if (isLLE) {
      return post(`${getLLEsubdomain('dash')}shopify-payment-api.katapult.com/api/v1/payment/resolve`, payload);
    }
    return post('shpfy.katapult.com/api/v1/payment/resolve', payload);
  };

  const rejectRequest = async payload => {
    if (isLLE) {
      return post(`${getLLEsubdomain('dash')}shopify-payment-api.katapult.com/api/v1/payment/reject`, payload);
    }
    return post('shpfy.katapult.com/api/v1/payment/reject', payload);
  };

  const authVerifyRequest = async payload => {
    return post(`${getLLEsubdomain('dot')}katapult.com/api/v3/user/auth/verify/`, payload);
  };

  const authRetailerRequest = async payload => {
    return post(`${getLLEsubdomain('dot')}katapult.com/api/v3/user/auth/retailer/`, payload);
  };

  const payrollFrequencyRequest = async payload => {
    return post(`${getLLEsubdomain('dot')}katapult.com/api/ng/payrollfrequency/`, payload);
  };

  const patchPayFrequency = async payload => {
    return patch(`${getLLEsubdomain('dot')}katapult.com/api/ng/payrollfrequency/`, payload);
  };

  const cartCheck = async payload => {
    return post(`${getLLEsubdomain('dot')}katapult.com/api/v3/application/cart_check/`, payload);
  };

  const addressCheck = async payload => {
    return post(`${getLLEsubdomain('dot')}katapult.com/api/ng/zibbyjs/application/addresscheck/`, payload);
  };

  const customerPhoneLookup = async payload => {
    return post(`${getLLEsubdomain('dot')}katapult.com/api/v3/user/auth/customer_phone_lookup/`, payload);
  };

  return {
    request: {
      applicationRequest,
      preapproveRequest,
      preapproveTmRequest,
      initializeRequest,
      priceDisclosureRequest,
      leaseAgreementRequest,
      resolveRequest,
      rejectRequest,
      authVerifyRequest,
      authRetailerRequest,
      payrollFrequencyRequest,
      patchPayFrequency,
      cartCheck,
      addressCheck,
      customerPhoneLookup,
    },
    loading,
    response,
    error,
  };
}
